/**
 * File helper utilities for device customization
 * Generic file handling functions for icon/image uploads
 */

/**
 * Maximum file size for icon uploads (500KB)
 */
export const MAX_FILE_SIZE = 500 * 1024;

/**
 * Allowed file types for icon uploads
 */
export const ALLOWED_FILE_TYPES = ['image/png', 'image/svg+xml', 'image/x-icon', 'image/vnd.microsoft.icon'];

/**
 * Allowed file extensions
 */
export const ALLOWED_EXTENSIONS = ['.png', '.svg', '.ico'];

/**
 * File validation result
 */
export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validate if a file meets requirements for icon upload
 *
 * @param file - File to validate
 * @returns Validation result with error message if invalid
 */
export function validateIconFile(file: File): FileValidationResult {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File size exceeds ${MAX_FILE_SIZE / 1024}KB limit. Current size: ${Math.round(file.size / 1024)}KB`,
    };
  }

  // Check file type
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file type. Allowed types: PNG, SVG, ICO`,
    };
  }

  // Check file extension
  const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  if (!ALLOWED_EXTENSIONS.includes(extension)) {
    return {
      valid: false,
      error: `Invalid file extension. Allowed extensions: ${ALLOWED_EXTENSIONS.join(', ')}`,
    };
  }

  return { valid: true };
}

/**
 * Convert a File to base64 string
 *
 * @param file - File to convert
 * @returns Promise that resolves to base64 string (without data URL prefix)
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/png;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Get file extension from filename
 *
 * @param filename - Filename to extract extension from
 * @returns File extension with dot (e.g., ".png")
 */
export function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1) return '';
  return filename.substring(lastDot).toLowerCase();
}

/**
 * Generate a safe filename from user input
 *
 * @param filename - Original filename
 * @returns Sanitized filename
 */
export function sanitizeFilename(filename: string): string {
  // Remove any path components
  const basename = filename.split(/[/\\]/).pop() || filename;

  // Replace spaces and special characters with underscores
  const sanitized = basename.replace(/[^a-zA-Z0-9._-]/g, '_');

  // Remove consecutive underscores
  return sanitized.replace(/_+/g, '_');
}

/**
 * Format file size for display
 *
 * @param bytes - File size in bytes
 * @returns Formatted file size string (e.g., "245 KB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${Math.round(kb)} KB`;
  }

  const mb = kb / 1024;
  return `${mb.toFixed(2)} MB`;
}

/**
 * Create a preview URL for an image file
 *
 * @param file - Image file
 * @returns Object URL that can be used in <img src="">
 */
export function createFilePreviewUrl(file: File): string {
  return URL.createObjectURL(file);
}

/**
 * Revoke a preview URL created by createFilePreviewUrl
 *
 * @param url - Object URL to revoke
 */
export function revokeFilePreviewUrl(url: string): void {
  URL.revokeObjectURL(url);
}

/**
 * Check if a file is an image
 *
 * @param file - File to check
 * @returns True if file is an image
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * Get MIME type from file extension
 *
 * @param extension - File extension (with or without dot)
 * @returns MIME type string
 */
export function getMimeTypeFromExtension(extension: string): string {
  const ext = extension.startsWith('.') ? extension : `.${extension}`;

  const mimeTypes: Record<string, string> = {
    '.png': 'image/png',
    '.svg': 'image/svg+xml',
    '.ico': 'image/x-icon',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.gif': 'image/gif',
    '.webp': 'image/webp',
  };

  return mimeTypes[ext.toLowerCase()] || 'application/octet-stream';
}

/**
 * Maximum file size for logo uploads (2MB)
 */
export const MAX_LOGO_FILE_SIZE = 2 * 1024 * 1024;

/**
 * Allowed file types for logo uploads
 */
export const ALLOWED_LOGO_FILE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/svg+xml',
  'image/webp',
];

/**
 * Allowed file extensions for logos
 */
export const ALLOWED_LOGO_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.svg', '.webp'];

/**
 * Validate if a file meets requirements for logo upload
 *
 * @param file - File to validate
 * @returns Validation result with error message if invalid
 */
export function validateLogoFile(file: File): FileValidationResult {
  // Check file size
  if (file.size > MAX_LOGO_FILE_SIZE) {
    return {
      valid: false,
      error: `File size exceeds ${formatFileSize(MAX_LOGO_FILE_SIZE)} limit. Current size: ${formatFileSize(file.size)}`,
    };
  }

  // Check file type
  if (!ALLOWED_LOGO_FILE_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file type. Allowed types: ${ALLOWED_LOGO_EXTENSIONS.join(', ')}`,
    };
  }

  // Check file extension
  const extension = getFileExtension(file.name);
  if (!ALLOWED_LOGO_EXTENSIONS.includes(extension)) {
    return {
      valid: false,
      error: `Invalid file extension. Allowed extensions: ${ALLOWED_LOGO_EXTENSIONS.join(', ')}`,
    };
  }

  return { valid: true };
}

/**
 * Maximum file size for Lottie uploads (1MB)
 */
export const MAX_LOTTIE_FILE_SIZE = 1 * 1024 * 1024;

/**
 * Allowed file types for Lottie uploads
 */
export const ALLOWED_LOTTIE_FILE_TYPES = ['application/json', 'text/plain'];

/**
 * Allowed file extensions for Lottie files
 */
export const ALLOWED_LOTTIE_EXTENSIONS = ['.json'];

/**
 * Validate if a file meets requirements for Lottie upload
 *
 * @param file - File to validate
 * @returns Validation result with error message if invalid
 */
export function validateLottieFile(file: File): FileValidationResult {
  // Check file size
  if (file.size > MAX_LOTTIE_FILE_SIZE) {
    return {
      valid: false,
      error: `File size exceeds ${formatFileSize(MAX_LOTTIE_FILE_SIZE)} limit. Current size: ${formatFileSize(file.size)}`,
    };
  }

  // Check file extension (more reliable than MIME type for JSON)
  const extension = getFileExtension(file.name);
  if (!ALLOWED_LOTTIE_EXTENSIONS.includes(extension)) {
    return {
      valid: false,
      error: `Invalid file extension. Allowed extensions: ${ALLOWED_LOTTIE_EXTENSIONS.join(', ')}`,
    };
  }

  // Check file type (allow both application/json and text/plain)
  if (!ALLOWED_LOTTIE_FILE_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file type. Expected JSON file for Lottie animation`,
    };
  }

  return { valid: true };
}

/**
 * Detect if a file is a Lottie animation based on extension
 *
 * @param file - File to check
 * @returns True if file appears to be a Lottie animation
 */
export function isLottieFile(file: File): boolean {
  const extension = getFileExtension(file.name);
  return ALLOWED_LOTTIE_EXTENSIONS.includes(extension);
}

/**
 * Validate media file (supports both images and Lottie animations)
 *
 * @param file - File to validate
 * @returns Validation result with error message if invalid
 */
export function validateMediaFile(file: File): FileValidationResult & { mediaType?: 'image' | 'lottie' } {
  // Determine media type based on file extension
  const isLottie = isLottieFile(file);

  if (isLottie) {
    const lottieValidation = validateLottieFile(file);
    return {
      ...lottieValidation,
      mediaType: 'lottie',
    };
  } else {
    const imageValidation = validateLogoFile(file);
    return {
      ...imageValidation,
      mediaType: 'image',
    };
  }
}
